import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import clsx from 'clsx'
import { Box, Table, TableBody, TableCell } from '@mui/material'
import { Icon, IconName } from 'src/components/atoms'
import {
  AppTableHead,
  AppTableRow,
  BadgeCell,
  CheckBoxCell,
  Column,
  IconCell,
  TextCell,
} from 'src/components/tables'
import cellStyles from 'src/components/tables/Cells/styles.module.scss'
import { ConsistencyEnum, getConsistencyIcon } from 'src/domain/consistency'
import {
  ENABLE_SEND_NOTIFICATION_ORG_CODES,
  isConsistencyResultHidden,
  VIEWABLE_CONTRACT_TYPE_AND_REGISTER_ORG_CODES,
} from 'src/domain/org'
import {
  isReportStatusBeforeReported,
  getReportStatusBadgeColor,
  getReportStatusFillTypeColor,
  getReportStatusTitle,
} from 'src/domain/reportStatus'
import {
  AdminAdminSalesReportManagerEditorSalesReportManagerRoles,
  AdminAdminSalesReportManagerEditorSalesReportManagerEditorStoreManagerInOperationCenterRoles,
  isOneOfRoles,
} from 'src/domain/role'
import {
  CheckStatusList,
  ValueType,
} from 'src/features/tenantSalesReportSummary/type'
import {
  FinishedFunnelEnum,
  ReportStatusEnum,
  RoleEnum,
  TenantSalesReportSummary,
} from 'src/slices/services/api'
import { formatDateTime } from 'src/utils/date'

import style from './styles.module.scss'

// NOTE: ドラッグ範囲選択中のオートスクロール設定。
// EDGE_THRESHOLD: 表示領域の上端／下端からこの距離(px)以内にポインタが来たらスクロールする。
// SPEED: 1秒あたりのスクロール量(px)。経過時間で掛けるため、リフレッシュレート
//         （60Hz/120Hz 等）に依存せず一定の体感速度になる。720px/秒 ≒ 従来の 12px/frame @60fps。
const AUTO_SCROLL_EDGE_THRESHOLD_PX = 48
const AUTO_SCROLL_SPEED_PX_PER_SEC = 720

export type TenantSalesReportSummaryTableProps = {
  summaries: TenantSalesReportSummary[]
  checkStatusList: CheckStatusList
  isAllChecked?: boolean
  role?: RoleEnum
  orgCode: string
  onChangeCheckStatus: (tenantCode: string) => void
  onChangeAllCheckStatus: () => void
  // NOTE: ドラッグ範囲選択。対象テナントコード群を isChecked の状態に揃える。
  onChangeRangeCheckStatus: (tenantCodes: string[], isChecked: boolean) => void
} & React.StyledProps

const TenantSalesReportSummaryTable: React.StyledFC<TenantSalesReportSummaryTableProps> =
  memo(
    ({
      className,
      summaries,
      checkStatusList,
      isAllChecked,
      role,
      orgCode,
      onChangeCheckStatus,
      onChangeAllCheckStatus,
      onChangeRangeCheckStatus,
    }: TenantSalesReportSummaryTableProps) => {
      const lastActionTime = useCallback(
        (summary: TenantSalesReportSummary): string => {
          if (
            summary.reportStatus !== ReportStatusEnum.Waiting &&
            summary.lastActionTime
          ) {
            return formatDateTime(summary.lastActionTime)
          }
          return '-'
        },
        []
      )

      const netSales = useCallback(
        (summary: TenantSalesReportSummary, valueType: ValueType): string => {
          if (
            summary.reportStatus === ReportStatusEnum.Waiting ||
            !summary.totals
          )
            return '-'
          const foundValue = summary.totals.find((value) => {
            return value.calculationItemCategoryName === 'net_sales'
          })
          if (!foundValue) return '-'
          const value =
            valueType === 'manual'
              ? foundValue.manualValue
              : foundValue.finalizedValue
          if (value !== 0 && !value) return '-'
          return value.toLocaleString()
        },
        []
      )

      const visitor = useCallback(
        (summary: TenantSalesReportSummary): string => {
          if (
            summary.reportStatus === ReportStatusEnum.Waiting ||
            !summary.totals
          )
            return '-'
          const foundValue = summary.totals.find((value) => {
            return value.calculationItemCategoryName === 'visitor'
          })
          if (
            !foundValue ||
            (foundValue.manualValue !== 0 && !foundValue.manualValue)
          )
            return '-'
          return foundValue.manualValue.toLocaleString()
        },
        []
      )

      const consistencyIcon = useCallback(
        (summary: TenantSalesReportSummary): IconName => {
          if (!summary.extractedConsistencyResult)
            return getConsistencyIcon(ConsistencyEnum.misMatch)

          if (summary.isAllManualInputValuesZero)
            return getConsistencyIcon(ConsistencyEnum.allMatchIncludingAllZero)

          return getConsistencyIcon(ConsistencyEnum.allMatchExcludingAllZero)
        },
        []
      )

      const isActiveCheckbox = useCallback(
        (summary: TenantSalesReportSummary) => {
          return !isReportStatusBeforeReported(summary.reportStatus)
        },
        []
      )

      // NOTE: ドラッグ範囲選択の状態管理。
      // dragStartIndex: ドラッグ開始行の index（null ならドラッグ中でない）
      // dragCurrentIndex: ドラッグ中に最後に通過した行の index
      // dragToChecked: 範囲を「選択(true)」「解除(false)」のどちらに揃えるか
      const [dragStartIndex, setDragStartIndex] = useState<number | null>(null)
      const [dragCurrentIndex, setDragCurrentIndex] = useState<number | null>(
        null
      )
      const dragToCheckedRef = useRef<boolean>(false)
      // NOTE: window の mouseup リスナーは最新の index を参照する必要があるため ref で保持する。
      const dragStartIndexRef = useRef<number | null>(null)
      const dragCurrentIndexRef = useRef<number | null>(null)
      // NOTE: 複数行ドラッグで範囲更新した直後は、続けて発火する click を1回だけ無視するためのフラグ。
      // state ではなく ref にすることで、mouseup → click の間で確実に（再レンダー待ちなしで）参照できる。
      const suppressNextClickRef = useRef<boolean>(false)
      // NOTE: テーブル本体(.root)への参照。実機ではこの要素が縦スクロールコンテナになる
      // （.root は overflow-x: auto 指定だが、CSS 仕様上 overflow-y も auto に計算されるため）。
      // ドラッグ中のオートスクロールでこの要素の scrollTop を操作する。
      const rootRef = useRef<HTMLDivElement>(null)
      // NOTE: ドラッグ中の最新ポインタ Y 座標（ビューポート基準 clientY）。
      // オートスクロール判定・現在行の算出に使う。座標系は getBoundingClientRect と
      // 揃えるため必ず clientY を使う。setState はせず ref に書くだけ。
      const pointerClientYRef = useRef<number | null>(null)
      // NOTE: オートスクロールの requestAnimationFrame の id。停止用に保持する。
      const rafIdRef = useRef<number | null>(null)
      // NOTE: rAF ループが毎フレーム呼ぶ「現在行の算出処理」の最新版を保持する ref。
      // ループの useEffect 依存に関数を入れず（毎フレームの再登録を防ぐ）、
      // 最新のクロージャをここから呼ぶことでレースを避ける。
      const updateRowFromPointerRef = useRef<() => void>(() => {})
      // NOTE: 各行の tr DOM を index で保持する。オートスクロール中に
      // getBoundingClientRect を走査して現在行を求めるために使う。
      const rowRefs = useRef<Map<number, HTMLTableRowElement>>(new Map())
      const isDragging = dragStartIndex !== null

      // NOTE: 開始行〜現在行の間の index 集合（昇順・降順どちらのドラッグも対応）。
      // 開始行と現在行が同じ間（＝まだ他行へドラッグしていない＝単純クリックになりうる状態）は
      // ハイライトを出さない。これでクリック選択時に一瞬だけ背景が青くなるのを防ぐ。
      const draggingIndexSet = useMemo(() => {
        if (
          dragStartIndex === null ||
          dragCurrentIndex === null ||
          dragStartIndex === dragCurrentIndex
        ) {
          return new Set<number>()
        }
        const from = Math.min(dragStartIndex, dragCurrentIndex)
        const to = Math.max(dragStartIndex, dragCurrentIndex)
        const set = new Set<number>()
        for (let i = from; i <= to; i += 1) {
          set.add(i)
        }
        return set
      }, [dragStartIndex, dragCurrentIndex])

      // NOTE: 行のどこを押下してもドラッグ開始とする（チェックボックス列に限定しない）。
      const handleMouseDownRow = useCallback(
        (
          event: React.MouseEvent,
          index: number,
          summary: TenantSalesReportSummary
        ) => {
          // NOTE: 左クリック（主ボタン）以外では開始しない。右クリック（コンテキスト
          // メニュー）や中クリックで意図しない範囲選択が始まるのを防ぐ。
          if (event.button !== 0) return
          if (!isActiveCheckbox(summary)) return
          // NOTE: 行のテキスト（テナント名など）が青く選択されるのを防ぐ。
          event.preventDefault()
          // NOTE: 開始行が未選択なら範囲を選択、選択済みなら解除に揃える。
          dragToCheckedRef.current = !checkStatusList[summary.tenant.code]
          dragStartIndexRef.current = index
          dragCurrentIndexRef.current = index
          // NOTE: 押下時点のポインタ Y 座標を入れておく。これがないと、押下後に
          // ポインタを動かさず静止したままだと mousemove が発火せず、端に居ても
          // オートスクロールが始まらない。
          pointerClientYRef.current = event.clientY
          setDragStartIndex(index)
          setDragCurrentIndex(index)
        },
        [checkStatusList, isActiveCheckbox]
      )

      const handleMouseEnterRow = useCallback((index: number) => {
        if (dragStartIndexRef.current === null) return
        // NOTE: rAF 経路（updateCurrentRowFromPointer）と二重に呼ばれても
        // 余分な再レンダーを出さないよう、同値なら何もしない。
        if (index === dragCurrentIndexRef.current) return
        dragCurrentIndexRef.current = index
        setDragCurrentIndex(index)
      }, [])

      // NOTE: ドラッグ状態を初期化する共通処理。
      const resetDragState = useCallback(() => {
        dragStartIndexRef.current = null
        dragCurrentIndexRef.current = null
        setDragStartIndex(null)
        setDragCurrentIndex(null)
      }, [])

      // NOTE: ドラッグ終了処理。
      // リスナーは常時登録し、ドラッグ中かどうかは ref（dragStartIndexRef）で判定する。
      // これにより「useEffect の登録が済む前に mouseup が来て isDragging が固着する」
      // という遅延登録レースを避ける。テーブル外で離した場合も window 経由で確実に走る。
      // さらに window の blur（別ウィンドウ・タブバー・OS 領域へフォーカスが移る）でも
      // 強制リセットし、DOM 外で mouseup したときの固着も防ぐ。
      useEffect(() => {
        const handleMouseUp = () => {
          const startIndex = dragStartIndexRef.current
          const currentIndex = dragCurrentIndexRef.current
          // NOTE: ドラッグ中でなければ何もしない。
          if (startIndex === null) return
          // NOTE: 単純クリック（同一行で開始・終了）は既存の onClick に委ねるため、
          // ここでは複数行をまたいだドラッグのときだけ範囲更新する。
          if (currentIndex !== null && startIndex !== currentIndex) {
            const from = Math.min(startIndex, currentIndex)
            const to = Math.max(startIndex, currentIndex)
            const tenantCodes = summaries
              .slice(from, to + 1)
              .map((summary) => summary.tenant.code)
            onChangeRangeCheckStatus(tenantCodes, dragToCheckedRef.current)
            // NOTE: mouseup の直後に発火する click（範囲選択の一部）だけを無視する。
            // click は mouseup と同じタスク内で同期的に発火するため、次のマクロタスクで
            // 必ず解放する。これで「行外で mouseup して click が来ない」場合にフラグが
            // 残り、後続の通常クリックが無反応になるのを防ぐ。
            suppressNextClickRef.current = true
            window.setTimeout(() => {
              suppressNextClickRef.current = false
            }, 0)
          }
          resetDragState()
        }

        window.addEventListener('mouseup', handleMouseUp)
        window.addEventListener('blur', resetDragState)
        return () => {
          window.removeEventListener('mouseup', handleMouseUp)
          window.removeEventListener('blur', resetDragState)
        }
      }, [summaries, onChangeRangeCheckStatus, resetDragState])

      // NOTE: 現在のポインタ Y 座標がどの行の上にあるかを求め、ドラッグ現在行として更新する。
      // オートスクロール中はポインタが静止したまま行が流れ込むため mouseenter が発火しない。
      // そこで各行の矩形(getBoundingClientRect)とポインタ Y を突き合わせて現在行を求める。
      // ・座標の真下要素を使う elementFromPoint は sticky ヘッダ・行間の隙間・別セルに
      //   阻まれて取りこぼすため使わない。
      // ・ポインタが表示範囲の下端より下／上端より上／行間の隙間にある場合は、
      //   可視の末尾／先頭の行に丸める。これで「下端でホバーし続ける間、スクロールで
      //   現れた行まで選択が伸び続ける」挙動になる。
      // 依存は空。中で読む可変値は全て ref のため関数の同一性は保たれる。
      const updateCurrentRowFromPointer = useCallback(() => {
        if (dragStartIndexRef.current === null) return
        const rawY = pointerClientYRef.current
        if (rawY === null) return
        const entries = Array.from(rowRefs.current.entries())
        if (entries.length === 0) return

        // NOTE: ポインタがテーブルの外（上下）にある場合でも、行探索はコンテナの
        // 表示範囲内の座標で行う。これにより「テーブル外へポインタを出すと未表示の
        // 行まで一気に選択される」のを防ぎ、可視範囲の端の行までに丸まる。
        const scrollEl = rootRef.current
        const containerRect = scrollEl?.getBoundingClientRect() ?? null
        const y = containerRect
          ? Math.min(Math.max(rawY, containerRect.top), containerRect.bottom)
          : rawY

        let nextIndex: number | null = null
        let topmostIndex: number | null = null
        let topmostTop = Infinity
        let bottommostIndex: number | null = null
        let bottommostBottom = -Infinity

        entries.forEach(([index, el]) => {
          const rect = el.getBoundingClientRect()
          // NOTE: コンテナの表示範囲と縦方向に重ならない行（スクロールで見切れて
          // いる行）は対象から除外する。これで丸め先が「可視範囲の端の行」になる。
          if (
            containerRect &&
            (rect.bottom <= containerRect.top ||
              rect.top >= containerRect.bottom)
          ) {
            return
          }
          if (rect.top < topmostTop) {
            topmostTop = rect.top
            topmostIndex = index
          }
          if (rect.bottom > bottommostBottom) {
            bottommostBottom = rect.bottom
            bottommostIndex = index
          }
          if (y >= rect.top && y < rect.bottom) {
            nextIndex = index
          }
        })

        // NOTE: どの行にも入っていない場合は可視範囲の端の行に丸める。
        if (nextIndex === null) {
          if (y >= bottommostBottom) {
            nextIndex = bottommostIndex
          } else if (y <= topmostTop) {
            nextIndex = topmostIndex
          } else {
            // NOTE: 行間のわずかな隙間。据え置き（チラつき防止）。
            return
          }
        }

        // NOTE: 変化したときだけ setState する（毎フレーム呼ばれても同値なら再レンダーしない）。
        if (nextIndex !== null && nextIndex !== dragCurrentIndexRef.current) {
          dragCurrentIndexRef.current = nextIndex
          setDragCurrentIndex(nextIndex)
        }
      }, [])

      // NOTE: rAF ループが最新の算出処理を参照できるよう、毎レンダーで ref に載せ替える。
      updateRowFromPointerRef.current = updateCurrentRowFromPointer

      // NOTE: ドラッグ中のオートスクロール。
      // ポインタがスクロールコンテナ(.container)の上端／下端付近に来ている間、
      // requestAnimationFrame で毎フレーム scrollTop を増減させ、見切れている行まで
      // スクロールしながら選択できるようにする。
      // 依存は [isDragging] のみ。ループが参照する可変値（ポインタ座標・現在行算出・
      // スクロール要素）は全て ref 経由で読むため、毎フレームの setState で再レンダーが
      // 起きても effect は張り直されない（＝rAF の cancel/再登録レースを避ける）。
      useEffect(() => {
        if (!isDragging) return undefined
        const scrollEl = rootRef.current
        if (!scrollEl) return undefined

        // NOTE: ポインタ座標を ref に保存するだけに留める（全行走査はしない）。
        // mousemove は高頻度で発火するため、ここで毎回全行の getBoundingClientRect を
        // 走査すると行数が多い組織で強制レイアウトが多発する。
        // 通常のドラッグ（端に居らずスクロールしない）での行通過検出は、行に入った
        // 瞬間に発火する onMouseEnter（handleMouseEnterRow）が担う。
        // 全行走査は rAF ループ内で「実際にスクロールしたフレーム」だけに限定する。
        const handleMouseMove = (event: MouseEvent) => {
          pointerClientYRef.current = event.clientY
        }
        window.addEventListener('mousemove', handleMouseMove)

        // NOTE: 経過時間ベースでスクロール量を決めるための、前フレームのタイムスタンプ。
        let prevTimestamp: number | null = null

        const tick = (timestamp: number) => {
          const delta =
            prevTimestamp === null ? 0 : (timestamp - prevTimestamp) / 1000
          prevTimestamp = timestamp

          const y = pointerClientYRef.current
          if (y !== null && delta > 0) {
            const rect = scrollEl.getBoundingClientRect()
            // NOTE: リフレッシュレートに依存しないよう、px/秒 × 経過秒でスクロール量を決める。
            const distance = AUTO_SCROLL_SPEED_PX_PER_SEC * delta
            const before = scrollEl.scrollTop
            if (y > rect.bottom - AUTO_SCROLL_EDGE_THRESHOLD_PX) {
              // NOTE: scrollTop は範囲外を代入してもブラウザ側で clamp される。
              scrollEl.scrollTop += distance
            } else if (y < rect.top + AUTO_SCROLL_EDGE_THRESHOLD_PX) {
              scrollEl.scrollTop -= distance
            }
            // NOTE: 実際にスクロールしたフレームだけ現在行を再計算する（毎フレームの
            // 全行走査を避け、ポインタ静止時の負荷を抑える）。通常のポインタ移動時の
            // 追従は mousemove 側の即時更新が担う。
            if (scrollEl.scrollTop !== before) {
              updateRowFromPointerRef.current()
            }
          }
          rafIdRef.current = requestAnimationFrame(tick)
        }
        rafIdRef.current = requestAnimationFrame(tick)

        return () => {
          window.removeEventListener('mousemove', handleMouseMove)
          if (rafIdRef.current !== null) {
            cancelAnimationFrame(rafIdRef.current)
          }
          rafIdRef.current = null
          pointerClientYRef.current = null
        }
        // NOTE: rootRef は RefObject で同一性が変わらないため依存に含めない。
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, [isDragging])

      const showConsistencyAndSalesValuesByRole = useCallback((): boolean => {
        return isOneOfRoles(
          AdminAdminSalesReportManagerEditorSalesReportManagerRoles,
          role
        )
      }, [role])

      const showMemoColumns = useCallback((): boolean => {
        return isOneOfRoles(
          AdminAdminSalesReportManagerEditorSalesReportManagerEditorStoreManagerInOperationCenterRoles,
          role
        )
      }, [role])

      const getColumns: Column[] = useMemo(() => {
        const columns: Column[] = [
          { title: 'ステータス' },
          { title: 'テナントコード' },
        ]

        if (VIEWABLE_CONTRACT_TYPE_AND_REGISTER_ORG_CODES.includes(orgCode)) {
          columns.push({ title: '代表レジ' })
        }

        columns.push({ title: 'テナント名称', isCenter: false })

        // 対象org（OPA: 006/011）では完全一致列を非表示にする
        if (
          showConsistencyAndSalesValuesByRole() &&
          !isConsistencyResultHidden(orgCode)
        ) {
          columns.push({ title: '完全一致', isCenter: true })
        }

        // メモ関連列は特定の権限では非表示
        if (showMemoColumns()) {
          columns.push(
            { title: '補足事項', isCenter: true },
            { title: '管理者メモ', isCenter: true }
          )

          if (ENABLE_SEND_NOTIFICATION_ORG_CODES.includes(orgCode)) {
            columns.push({ title: 'メッセージ', isCenter: true })
          }
        }

        if (showConsistencyAndSalesValuesByRole()) {
          columns.push(
            { title: '純売上(報告値)' },
            { title: '純売上(確定値)' },
            { title: '客数(報告値)' }
          )
        }

        columns.push({ title: '最終作業時間' }, { title: '備考' })
        return columns
      }, [showConsistencyAndSalesValuesByRole, showMemoColumns, orgCode])

      const getExitPageDescription = (
        summary: TenantSalesReportSummary
      ): string => {
        if (summary.reportStatus !== ReportStatusEnum.LeftHalfway) return '-'

        const funnel = summary.lastFinishedFunnel
        const countRegister = summary.countRegisters
        switch (funnel) {
          case FinishedFunnelEnum.SelectingSalesDate:
          case FinishedFunnelEnum.AddingRegister:
            return `${countRegister}台目がレシート画像登録画面で中断`
          case FinishedFunnelEnum.UploadingImages:
            return `${countRegister}台目が売上入力画面で中断`
          case FinishedFunnelEnum.SendingManualInputValues:
            return `${countRegister}台目がレジ分岐画面で中断`
          default:
            return '-'
        }
      }

      return (
        <Box ref={rootRef} className={clsx(className, style.root)}>
          <Table className={clsx(className, style.table)} stickyHeader>
            <AppTableHead
              isChecked={isAllChecked}
              checkable
              onChange={onChangeAllCheckStatus}
              columns={getColumns}
            />
            <TableBody>
              {summaries.map((summary, index) => {
                const active = isActiveCheckbox(summary)
                // NOTE: ドラッグ範囲内かつ選択可能な行は、確定前でも見た目に反映する。
                const inDragRange = active && draggingIndexSet.has(index)
                const isChecked = inDragRange
                  ? dragToCheckedRef.current
                  : Boolean(checkStatusList[summary.tenant.code])
                return (
                  <AppTableRow
                    key={summary.id}
                    rowRef={(el) => {
                      if (el) {
                        rowRefs.current.set(index, el)
                      } else {
                        rowRefs.current.delete(index)
                      }
                    }}
                    onClick={() => {
                      // NOTE: 複数行ドラッグで範囲更新した直後の click は無視する（1回だけ）。
                      // state ではなく ref を見るため、window mouseup の状態反映を待たずに
                      // 同期的に判定でき、単純クリックが無反応になるレースを避けられる。
                      if (suppressNextClickRef.current) {
                        suppressNextClickRef.current = false
                        return
                      }
                      if (active) {
                        onChangeCheckStatus(summary.tenant.code)
                      }
                    }}
                    onMouseDown={(event) =>
                      handleMouseDownRow(event, index, summary)
                    }
                    onMouseEnter={() => handleMouseEnterRow(index)}
                    className={clsx(
                      style.row,
                      active && style.activeRow,
                      isDragging && style.dragging,
                      inDragRange && style.dragRangeRow
                    )}
                  >
                    {active ? (
                      <CheckBoxCell isChecked={isChecked} />
                    ) : (
                      <TextCell text="" />
                    )}

                    <BadgeCell
                      className={style.badge}
                      label={getReportStatusTitle(summary.reportStatus)}
                      color={getReportStatusBadgeColor(summary.reportStatus)}
                      fillType={getReportStatusFillTypeColor(
                        summary.reportStatus
                      )}
                      ellipse
                    />
                    <TextCell
                      text={summary.tenant.code}
                      className={style.text}
                    />
                    {VIEWABLE_CONTRACT_TYPE_AND_REGISTER_ORG_CODES.includes(
                      orgCode
                    ) && (
                      <TextCell
                        text={summary.representativeTenantRegisterNumber || '-'}
                        className={style.text}
                      />
                    )}
                    <TextCell
                      text={summary.tenant.name}
                      className={style.text}
                      width={180}
                      tooltipIconText={
                        summary.tenant.isMonthlyReport
                          ? '月報テナント'
                          : undefined
                      }
                    />
                    {showConsistencyAndSalesValuesByRole() &&
                      !isConsistencyResultHidden(orgCode) &&
                      (isReportStatusBeforeReported(summary.reportStatus) ? (
                        <TextCell
                          className={clsx(style.center, style.text)}
                          text={
                            summary.reportStatus === ReportStatusEnum.Waiting
                              ? ''
                              : '-'
                          }
                        />
                      ) : (
                        <IconCell
                          className={clsx(
                            style.center,
                            style.icon,
                            style.green
                          )}
                          icon={consistencyIcon(summary)}
                        />
                      ))}
                    {showMemoColumns() && (
                      <>
                        {Boolean(summary.reportMessage) ? (
                          <IconCell
                            className={clsx(style.center, style.icon)}
                            icon="memo"
                          />
                        ) : (
                          <TextCell
                            text="-"
                            className={clsx(style.center, style.text)}
                          />
                        )}
                        {Boolean(summary.manageMessage) ||
                        Boolean(summary.tenantNote) ? (
                          <TableCell
                            className={clsx(cellStyles.cell, style.center)}
                          >
                            <Box className={style.manageMemoIcons}>
                              {Boolean(summary.manageMessage) && (
                                <Box className={clsx(style.icon, style.blue)}>
                                  <Icon icon="eye" />
                                </Box>
                              )}
                              {Boolean(summary.tenantNote) && (
                                <Box
                                  className={clsx(
                                    style.icon,
                                    style.blue,
                                    style.tenantMemoIcon
                                  )}
                                >
                                  <Icon icon="note2" />
                                </Box>
                              )}
                            </Box>
                          </TableCell>
                        ) : (
                          <TextCell
                            text="-"
                            className={clsx(style.center, style.text)}
                          />
                        )}
                        {ENABLE_SEND_NOTIFICATION_ORG_CODES.includes(orgCode) &&
                          (Boolean(summary.isNotificationSent) ? (
                            <IconCell
                              className={clsx(style.center, style.icon)}
                              icon="exclamation"
                            />
                          ) : (
                            <TextCell
                              text="-"
                              className={clsx(style.center, style.text)}
                            />
                          ))}
                      </>
                    )}
                    {showConsistencyAndSalesValuesByRole() && (
                      <>
                        <TextCell
                          text={netSales(summary, 'manual')}
                          className={style.number}
                          width={112}
                          unit={
                            netSales(summary, 'manual') !== '-'
                              ? '円'
                              : undefined
                          }
                        />
                        <TextCell
                          text={netSales(summary, 'finalized')}
                          className={style.number}
                          width={112}
                          unit={
                            netSales(summary, 'finalized') !== '-'
                              ? '円'
                              : undefined
                          }
                        />
                        <TextCell
                          text={visitor(summary)}
                          className={style.number}
                          width={112}
                          unit={visitor(summary) !== '-' ? '人' : undefined}
                        />
                      </>
                    )}
                    <TextCell
                      text={lastActionTime(summary)}
                      className={style.text}
                      width={120}
                    />
                    <TextCell
                      text={getExitPageDescription(summary)}
                      className={style.text}
                      width={220}
                    />
                  </AppTableRow>
                )
              })}
            </TableBody>
          </Table>
        </Box>
      )
    }
  )

TenantSalesReportSummaryTable.displayName = 'TenantSalesReportSummaryTable'
export default TenantSalesReportSummaryTable
